import { useEffect, useState } from 'react';
import { useOnboardingState, useOnboardingDispatch } from '../../context/OnboardingContext';
import { useStepNavigation } from '../../hooks/useStepNavigation';
import { useOnboardingBlocker } from '../../hooks/useOnboardingBlocker';
import FileDropZone from '../../components/onboarding/FileDropZone';
import VatDeclarationSection from '../../components/onboarding/VatDeclarationSection';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import { uploadDocument } from '../../services/uploadService';
import { useLanguage } from '../../i18n/LanguageContext';
import MissingFieldsHint from '../../components/onboarding/MissingFieldsHint';

export default function LicenseAndIdStep() {
  const state = useOnboardingState();
  const dispatch = useOnboardingDispatch();
  const { goNext } = useStepNavigation();
  const auth = useAuth();
  const { t } = useLanguage();
  useOnboardingBlocker();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vatQuestionError, setVatQuestionError] = useState<string | null>(null);
  const [trnError, setTrnError] = useState<string | null>(null);

  // Defensive default: drafts persisted before Spec 051 (and older test fixtures)
  // have no vatDeclaration slice.
  const vat = state.vatDeclaration ?? { vatRegistered: null, trn: '', vatRegisteredFrom: '' };

  // Hydrate the owner Emirates ID slice from the backend so a returning partner
  // (post-refresh, post-localStorage clear, or different device) sees what they
  // already uploaded. The trade license slice is hydrated by the parent loader
  // from GarageDetailResponse. Always fetch on mount — backend is source of truth.
  useEffect(() => {
    if (!auth.token) return;
    api.listMyDocuments(auth.token).then((docs) => {
      const latest = docs
        .filter((d) => d.documentType === 'OWNER_EMIRATES_ID' && d.status !== 'REJECTED')
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
      if (latest) {
        dispatch({
          type: 'SET_OWNER_EMIRATES_ID',
          data: {
            files: [{
              mediaId: latest.id,
              storageKey: latest.storageKey,
              fileName: latest.originalFilename || 'emirates-id',
              downloadUrl: latest.downloadUrl || '',
            }],
          },
        });
      }
    }).catch(() => { /* non-fatal: partner can re-upload */ });
  }, [auth.token, dispatch]);

  const hasLicense = state.tradeLicense.licenseNumber.trim() !== '' && state.tradeLicense.files.length > 0;
  const hasEmiratesId = state.ownerEmiratesId.files.length > 0;
  const canContinue = hasLicense && hasEmiratesId;

  // #430 — name the mandatory items still missing so the partner knows what to fix.
  const missingFields: string[] = [];
  if (!hasLicense) missingFields.push(t('onboarding.tradeLicense.document') || 'Trade license (number + document)');
  if (!hasEmiratesId) missingFields.push(t('onboarding.ownerEmiratesId.document') || 'Owner Emirates ID');

  const uploadEmiratesId = async (file: File) => {
    if (!auth.token) throw new Error('Not authenticated');
    return uploadDocument(file, 'OWNER_EMIRATES_ID', auth.token);
  };

  const handleContinue = async () => {
    if (!canContinue || !auth.token) return;

    // Spec 051 AC-17 — the VAT question is MANDATORY: block proceeding with an
    // inline validation error on the question itself. The TRN stays optional
    // (approval-gated only) but must be exactly 15 digits when provided.
    setVatQuestionError(null);
    setTrnError(null);
    if (vat.vatRegistered === null) {
      setVatQuestionError('Please answer this question to continue — select Yes or No.');
      return;
    }
    if (vat.vatRegistered === true && vat.trn !== '' && !/^[0-9]{15}$/.test(vat.trn)) {
      setTrnError('TRN must be exactly 15 digits — or leave it empty to provide it later.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.updateDraft({
        licenseNumber: state.tradeLicense.licenseNumber,
        vatRegistered: vat.vatRegistered,
        trn: vat.vatRegistered && vat.trn !== '' ? vat.trn : null,
        vatRegisteredFrom: vat.vatRegistered && vat.vatRegisteredFrom !== '' ? vat.vatRegisteredFrom : null,
        onboardingStep: 2,
      }, auth.token);
      goNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 className="wizard-step-title" style={{ marginBottom: 0 }}>{t('onboarding.licenseAndId.title')}</h2>
        <button className="wizard-btn wizard-btn-primary" disabled={!canContinue || saving} onClick={handleContinue}>
          {saving ? t('common.saving') : t('common.next')}
        </button>
      </div>

      {/* ─── Trade License section ─── */}
      <div className="wizard-form-group">
        <label>{t('onboarding.tradeLicense.licenseNumber')}</label>
        <input
          type="text"
          placeholder={t('onboarding.tradeLicense.licenseNumber.placeholder')}
          value={state.tradeLicense.licenseNumber}
          onChange={(e) =>
            dispatch({
              type: 'SET_TRADE_LICENSE',
              data: { ...state.tradeLicense, licenseNumber: e.target.value },
            })
          }
        />
      </div>

      <div className="wizard-form-group">
        <label>{t('onboarding.tradeLicense.document')}</label>
        <FileDropZone
          mediaType="TRADE_LICENSE"
          token={auth.token || ''}
          accept="application/pdf,image/jpeg,image/png"
          maxFiles={1}
          label={t('onboarding.tradeLicense.dropLabel')}
          sublabel={t('onboarding.tradeLicense.dropSublabel')}
          files={state.tradeLicense.files}
          onUploadComplete={(file) =>
            dispatch({
              type: 'SET_TRADE_LICENSE',
              data: {
                ...state.tradeLicense,
                files: [...state.tradeLicense.files, file],
              },
            })
          }
          onRemove={(mediaId) =>
            dispatch({
              type: 'SET_TRADE_LICENSE',
              data: {
                ...state.tradeLicense,
                files: state.tradeLicense.files.filter((f) => f.mediaId !== mediaId),
              },
            })
          }
        />
      </div>

      {/* ─── Name-match note ─── */}
      <div
        role="note"
        style={{
          background: '#fff8e1',
          border: '1px solid #f0c36d',
          borderRadius: '6px',
          padding: '0.75rem 1rem',
          margin: '1.5rem 0',
          fontSize: '0.9rem',
          color: '#6b4f00',
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'flex-start',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: '1.1rem', lineHeight: 1 }}>ⓘ</span>
        <span>{t('onboarding.licenseAndId.nameMatchNote')}</span>
      </div>

      {/* ─── Owner Emirates ID section ─── */}
      <div className="wizard-form-group">
        <label>{t('onboarding.ownerEmiratesId.document')}</label>
        <FileDropZone
          mediaType="OWNER_EMIRATES_ID"
          token={auth.token || ''}
          accept="application/pdf,image/jpeg,image/png"
          maxFiles={1}
          label={t('onboarding.ownerEmiratesId.dropLabel')}
          sublabel={t('onboarding.ownerEmiratesId.dropSublabel')}
          files={state.ownerEmiratesId.files}
          uploadFn={uploadEmiratesId}
          onUploadComplete={(file) =>
            dispatch({
              type: 'SET_OWNER_EMIRATES_ID',
              data: {
                files: [...state.ownerEmiratesId.files, file],
              },
            })
          }
          onRemove={(mediaId) =>
            dispatch({
              type: 'SET_OWNER_EMIRATES_ID',
              data: {
                files: state.ownerEmiratesId.files.filter((f) => f.mediaId !== mediaId),
              },
            })
          }
        />
      </div>

      {/* ─── VAT registration declaration (Spec 051 Flow F / AC-17) ─── */}
      <VatDeclarationSection
        value={vat}
        onChange={(data) => {
          if (data.vatRegistered !== undefined) setVatQuestionError(null);
          if (data.trn !== undefined) setTrnError(null);
          dispatch({ type: 'SET_VAT_DECLARATION', data });
        }}
        questionError={vatQuestionError}
        trnError={trnError}
      />

      {!canContinue && <MissingFieldsHint missing={missingFields} />}

      {error && <div className="error-message">{error}</div>}
    </div>
  );
}
